import { z } from "zod";
import { formConfigToZodSchemas, getFormConfigDefaultValues } from "../form-config-to-zod-schema";
import {
  getFormKeys,
  type FormConfig,
  type FormConfigInPages,
  type I18NFunction,
  type SectionConfig,
} from "../form-config-types";
import {
  mapToNumPatientsEnumLabels,
  mapToStandardPercentageEnumLabels,
  mapToTypesOfSpecialistsEnumLabels,
  mapToTypesOfSpecialistsInvolvedInTheMDTEnumLabels,
  mapToYesNoEnumLabels,
  numPatientsEnum,
  standardPercentageEnum,
  typesOfSpecialistsEnum,
  typesOfSpecialistsInvolvedInTheMDTEnum,
  yesNoEnum,
} from "./form-data-enums";
import { enumErrorMap } from "./form-data-utils";

const treatmentLateStageFormConfigInPages = (l: I18NFunction) =>
  [
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 4.1 Please describe how the MDT works together at your centre to support the management of patients in the advanced NMSC setting. [open box]
        mdtWorksTogether: {
          type: "textarea",
          label: l(
            "1. Please describe how the MDT works together at your centre to support the management of patients in the advanced NMSC (CSCC/BCC) setting.",
            "1. Veuillez décrire comment l'EMD travaille ensemble à votre centre pour soutenir la prise en charge des patients atteints de NMSC (CSCC/BCC) de stade avancé."
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, {
              message: l("Please enter a value", "Ce champ est requis"),
            }),
        },

        // 4.2 What factors determine your treatment decisions in advanced NMSC patients? [open box] [Not applicable; if selected, go to question 4.4]
        factorsDetermineTreatmentDecisions: {
          type: "object",
          label: l(
            "2. What factors determine your treatment decisions in advanced NMSC (CSCC/BCC) patients?",
            "2. Quels facteurs déterminent vos décisions thérapeutiques pour les patients atteints de NMSC (CSCC/BCC) de stade avancé?"
          ),
          offerNotApplicableForEntireObject: true,
          properties: {
            factorsDetermineTreatmentDecisions: {
              type: "textarea",
              label: "",
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
              hideNotApplicableCheckbox: true,
            },
            // 4.3 Do these factors differ between academic and community centers? [Yes or No]
            // [If selected Yes] Please explain the differences. [open box]

            doTheseFactorsDifferBetweenAcademicAndCommunityCenters: {
              type: "radioGroup",
              label: l(
                "3. Do these factors differ between academic and community centers?",
                "3. Ces facteurs diffèrent-ils entre les centres universitaires et communautaires?"
              ),
              schema: z
                .enum(yesNoEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToYesNoEnumLabels,
              hideNotApplicableCheckbox: true,
              cols: 2,
            },
            pleaseExplainTheDifferences: {
              type: "textarea",
              label: l("Please explain the differences.", "Veuillez expliquer les différences."),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              hideNotApplicableCheckbox: true,
              dependencies: [
                {
                  if: [
                    "factorsDetermineTreatmentDecisions.doTheseFactorsDifferBetweenAcademicAndCommunityCenters",
                    "not-equals",
                    "Yes",
                  ],
                  then: "hide",
                },
                {
                  if: [
                    "factorsDetermineTreatmentDecisions.doTheseFactorsDifferBetweenAcademicAndCommunityCenters",
                    "equals",
                    "Yes",
                  ],
                  then: "require",
                },
              ],
              // hideIf: [
              //   "factorsDetermineTreatmentDecisions.doTheseFactorsDifferBetweenAcademicAndCommunityCenters",
              //   "not-equals",
              //   "Yes",
              // ],
              // requireIf: [
              //   "factorsDetermineTreatmentDecisions.doTheseFactorsDifferBetweenAcademicAndCommunityCenters",
              //   "equals",
              //   "Yes",
              // ],
            },
            // Is it the same for BCC? [Yes or No]
            // [If selected No] Please explain the differences. [open box]
            isItTheSameForBCC: {
              type: "radioGroup",
              label: l("Is it the same for BCC?", "Est-ce la même chose pour le BCC?"),
              schema: z
                .enum(yesNoEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToYesNoEnumLabels,
              hideNotApplicableCheckbox: true,
              cols: 2,
            },
            pleaseExplainTheDifferencesForBCC: {
              type: "textarea",
              label: l("Please explain the differences.", "Veuillez expliquer les différences."),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              hideNotApplicableCheckbox: true,
              dependencies: [
                {
                  if: ["factorsDetermineTreatmentDecisions.isItTheSameForBCC", "not-equals", "No"],
                  then: "hide",
                },
                {
                  if: ["factorsDetermineTreatmentDecisions.isItTheSameForBCC", "equals", "No"],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 4.4 Who is involved in the treatment decision making for advanced NMSC patients and when do they become involved in the decision-making process?
        // PCP/GPs [checkbox]
        // [If checked] Please specify when PCP/GPs become involved. [open box]
        // Dermatologists [checkbox]
        // [If checked] Please specify when dermatologists become involved. [open box]
        // ENTs [checkbox]
        // [If checked] Please specify when ENTs become involved. [open box]
        // Surgeons [checkbox]
        // [If checked] Please specify when surgeons become involved. [open box]
        // Radio oncologists [checkbox]
        // [If checked] Please specify when radio oncologists become involved. [open box]
        // Pathologists [checkbox]
        // [If checked] Please specify when pathologists become involved. [open box]
        // Medical oncologists [checkbox]
        // [If checked] Please specify when medical oncologists become involved. [open box]
        // Others [checkbox]
        // [If checked] Please specify. [open box]
        whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients: {
          type: "object",
          label: l(
            "4. Who is involved in the treatment decision making for advanced NMSC (CSCC/BCC) patients and when do they become involved in the decision-making process?",
            "4. Qui participe à la prise de décisions thérapeutiques pour les patients atteints de NMSC (CSCC/BCC) de stade avancé et quand s'impliquent-ils dans le processus décisionnel?"
          ),
          properties: {
            //
            // PCPs
            //
            primaryCarePhysicians: {
              type: "singleCheckbox",
              label: l("Primary Care Physicians", "Médecins généralistes"),
              schema: z.boolean().nullish(),
            },
            primaryCarePhysiciansWhen: {
              type: "textarea",
              label: l(
                "Please specify when Primary Care Physicians become involved.",
                "Veuillez préciser à quel moment sont impliqués les médecins généralistes."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.primaryCarePhysicians",
                    "equals",
                    true,
                  ],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.primaryCarePhysicians",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },
            //
            // Dermatologists
            //
            dermatologists: {
              type: "singleCheckbox",
              label: l("Dermatologists", "Dermatologues"),
              schema: z.boolean().nullish(),
            },
            dermatologistsWhen: {
              type: "textarea",
              label: l(
                "Please specify when Dermatologists become involved.",
                "Veuillez préciser à quel moment sont impliqués les dermatologues."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.dermatologists",
                    "equals",
                    true,
                  ],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.dermatologists",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },
            //
            // ENTs
            //
            ENTs: {
              type: "singleCheckbox",
              label: l("ENTs", "ORL"),
              schema: z.boolean().nullish(),
            },
            ENTsWhen: {
              type: "textarea",
              label: l(
                "Please specify when ENTs become involved.",
                "Veuillez préciser à quel moment sont impliqués les ORL."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.ENTs", "equals", true],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.ENTs",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },

            //
            // surgical oncologists
            //
            surgicalOncologists: {
              type: "singleCheckbox",
              label: l("Surgical Oncologists", "Oncologues chirurgicaux"),
              schema: z.boolean().nullish(),
            },
            surgicalOncologistsWhen: {
              type: "textarea",
              label: l(
                "Please specify when surgical oncologists become involved.",
                "Veuillez préciser à quel moment sont impliqués les oncologues chirurgicaux."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.surgicalOncologists",
                    "equals",
                    true,
                  ],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.surgicalOncologists",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },

            //
            // Radiation Oncologists
            //
            radiationOncologists: {
              type: "singleCheckbox",
              label: l("Radiation Oncologists", "Radio-oncologues"),
              schema: z.boolean().nullish(),
            },
            radiationOncologistsWhen: {
              type: "textarea",
              label: l(
                "Please specify when radiation oncologists become involved.",
                "Veuillez préciser à quel moment sont impliqués les radio-oncologues."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.radiationOncologists",
                    "equals",
                    true,
                  ],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.radiationOncologists",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },

            //
            // Pathologists
            //
            pathologists: {
              type: "singleCheckbox",
              label: l("Pathologists", "Pathologistes"),
              schema: z.boolean().nullish(),
            },
            pathologistsWhen: {
              type: "textarea",
              label: l(
                "Please specify when pathologists become involved.",
                "Veuillez préciser à quel moment sont impliqués les pathologistes."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.pathologists",
                    "equals",
                    true,
                  ],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.pathologists",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },

            //
            // Medical Oncologists
            //
            medicalOncologists: {
              type: "singleCheckbox",
              label: l("Medical Oncologists", "Oncologues médicaux"),
              schema: z.boolean().nullish(),
            },
            medicalOncologistsWhen: {
              type: "textarea",
              label: l(
                "Please specify when medical oncologists become involved.",
                "Veuillez préciser à quel moment sont impliqués les oncologues médicaux."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.medicalOncologists",
                    "equals",
                    true,
                  ],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.medicalOncologists",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },

            //
            // Others
            //
            others: {
              type: "singleCheckbox",
              label: l("Others", "Autres"),
              schema: z.boolean().nullish(),
            },
            othersWhen: {
              type: "textarea",
              label: l(
                "Please specify when others become involved.",
                "Veuillez préciser à quel moment d'autres sont impliqués."
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .nullish(),
              dependencies: [
                {
                  if: ["whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.others", "equals", true],
                  then: "require",
                },
                {
                  if: [
                    "whoAndWhenInvolvedInTheTreatmentDecisionMakingForAdvancedNMScPatients.others",
                    "not-equals",
                    true,
                  ],
                  then: "hide",
                },
              ],
            },
          },
        },
      },
    },
    // page

    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 4.5 What types of clinical cases and considerations related to advanced NMSC would you bring to a MDT discussion? [open box]
        whatTypesOfClinicalCasesAndConsiderationsRelatedToAdvancedNMScWouldYouBringToAMDTDiscussion: {
          type: "textarea",
          label: l(
            "5. When considering a patient case with advanced NMSC (CSCC/BCC), what factors and clinical considerations would lead you to bring the case to an MDT tumor board meeting?",
            "5. Lorsque vous envisagez un cas de NMSC (CSCC/BCC) de stade avancé, quels facteurs et considérations cliniques vous inciteraient à amener ce cas à une réunion multidisciplinaire?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, l("Please enter a value", "Ce champ est requis")),
        },

        // 4.6 When considering a patient case with advanced NMSC, what factors lead you to consult with another specialty? [open box]
        whenConsideringAPatientCaseWithAdvancedNMScWhatFactorsLeadYouToConsultWithAnotherSpecialty: {
          type: "textarea",
          label: l(
            "6. When considering a patient case with advanced NMSC (CSCC/BCC), what factors lead you to consult with another specialty?",
            "6. Lorsque vous envisagez de traiter un patient atteint de NMSC (CSCC/BCC) de stade avancé, quels facteurs vous incitent à consulter une autre spécialité?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .min(1, l("Please enter a value", "Ce champ est requis")),
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 4.7 For locally advanced, surgically operable patients with CSCC, are these patients automatically considered 'high-risk'?' [Yes or No] [Not applicable; If selected, go to question 4.8]
        // Are there additional factors that are considered? [open box]
        // Is it the same for BCC? [Yes or No]
        // [If selected No] Please explain the differences [open box]
        locallyAdvancedSurgicallyOperablePatientsWithCSCC: {
          type: "object",
          label: l(
            "7. For locally advanced, surgically operable patients with CSCC, are these patients automatically considered 'high-risk'?",
            "7. Pour les patients atteints de CSCC localement avancés qui sont chirurgicalement opérables, ces patients sont-ils automatiquement considérés à « risque élevé »?"
          ),
          offerNotApplicableForEntireObject: true,
          properties: {
            areThesePatientsAutomaticallyConsideredHighRisk: {
              type: "radioGroup",
              label: "",
              schema: z
                .enum(yesNoEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToYesNoEnumLabels,
              hideNotApplicableCheckbox: true,
              cols: 2,
            },
            areThereAdditionalFactorsThatAreConsidered: {
              type: "textarea",
              label: l(
                "Are there additional factors that are considered?",
                "D'autres facteurs sont-ils pris en compte?"
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
              hideNotApplicableCheckbox: true,
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 4.7 For locally advanced, surgically operable patients with CSCC, are these patients automatically considered 'high-risk'?  [Yes or No] [Not applicable; If selected, go to question 4.8]
        // Are there additional factors that are considered? [open box]
        // Is it the same for BCC? [Yes or No]
        // [If selected No] Please explain the differences [open box]
        whatCriteriaAreConsideredToDetermineWhetherAPatientHasResectableCSCCOrWhetherSystemicTherapyShouldBeConsidered:
          {
            type: "object",
            label: l(
              "8. What criteria are considered to determine whether a patient has resectable CSCC or whether systemic therapy should be considered?",
              "8. Quels critères sont envisagés pour déterminer si un patient présente un CSCC résécable ou si un traitement systémique doit être envisagé?"
            ),
            offerNotApplicableForEntireObject: true,
            properties: {
              whatCriteriaAreConsideredToDetermineResectableCSCC: {
                type: "textarea",
                label: "",
                schema: z
                  .string({
                    required_error: l("Please enter a value", "Ce champ est requis"),
                    invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                  })
                  .or(z.literal("not_applicable")),
                hideNotApplicableCheckbox: true,
              },
              whatDeterminesIneligibilityForSurgery: {
                type: "textarea",
                label: l(
                  "What determines ineligibility for surgery?",
                  "Qu'est-ce qui détermine l'inadmissibilité à la chirurgie?"
                ),
                schema: z
                  .string({
                    required_error: l("Please enter a value", "Ce champ est requis"),
                    invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                  })
                  .or(z.literal("not_applicable")),
                hideNotApplicableCheckbox: true,
              },
              isItTheSameForBCC: {
                type: "radioGroup",
                label: l("Is it the same for BCC?", "Est-ce la même chose pour le BCC?"),
                schema: z
                  .enum(yesNoEnum, {
                    errorMap: enumErrorMap(l),
                  })
                  .or(z.literal("not_applicable")),
                mapToLabels: mapToYesNoEnumLabels,
                hideNotApplicableCheckbox: true,
                cols: 2,
              },
              pleaseExplainTheDifferencesForBCC: {
                type: "textarea",
                label: l("Please explain the differences", "Veuillez expliquer les différences."),
                schema: z
                  .string({
                    required_error: l("Please enter a value", "Ce champ est requis"),
                    invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                  })
                  .nullish(),
                hideNotApplicableCheckbox: true,
                dependencies: [
                  {
                    if: [
                      "whatCriteriaAreConsideredToDetermineWhetherAPatientHasResectableCSCCOrWhetherSystemicTherapyShouldBeConsidered.isItTheSameForBCC",
                      "equals",
                      "No",
                    ],
                    then: "require",
                  },
                  {
                    if: [
                      "whatCriteriaAreConsideredToDetermineWhetherAPatientHasResectableCSCCOrWhetherSystemicTherapyShouldBeConsidered.isItTheSameForBCC",
                      "not-equals",
                      "No",
                    ],
                    then: "hide",
                  },
                ],
              },
            },
          },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 4.9 What proportion of locally advanced, surgically operable NMSC patients receive RT post-surgery? [Not applicable; If selected, go to section 5]
        // CSCC: [Selection of 1-100]
        // BCC: [Selection of 1-100]
        whatProportionOfLocallyAdvancedSurgicallyOperableNMScPatientsReceiveRTPostSurgery: {
          type: "object",
          label: l(
            "9. What proportion of locally advanced, surgically operable NMSC (CSCC/BCC) patients receive RT post-surgery?",
            "9. Quelle proportion de patients atteints de NMSC (CSCC/BCC) localement avancés et opérables reçoit une radiothérapie après une intervention chirurgicale?"
          ),
          /**
           * TODO: BUG: 4.8 and 4.9 not applicables are acting like the same field... why???????
           * May 16, 2025: Moving to separate pages for now. Weird....
           */
          offerNotApplicableForEntireObject: true,
          properties: {
            cscc: {
              type: "radioGroup",
              label: l("CSCC", "CSCC"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
              hideNotApplicableCheckbox: true,
            },
            bcc: {
              type: "radioGroup",
              label: l("BCC", "BCC"),
              schema: z
                .enum(standardPercentageEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToStandardPercentageEnumLabels,
              hideNotApplicableCheckbox: true,
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 4.1 Please describe how the MDT works together at your centre to support the management of patients in the advanced NMSC setting. [open box]
        whatPercentageOfYourAdvancedBCCOnHHITherapy: {
          type: "radioGroup",
          label: l(
            "10. What percentage of your advanced BCC patients are on HHI therapy?",
            "10. Quelle proportion de vos patients atteints de BCC de stade avancé reçoit une thérapie à base de hedgehog inhibitors (HHI)?"
          ),
          schema: z.enum(standardPercentageEnum, {
            errorMap: enumErrorMap(l),
          }),
          mapToLabels: mapToStandardPercentageEnumLabels,
        },

        // 4.2 What factors determine your treatment decisions in advanced NMSC patients? [open box] [Not applicable; if selected, go to question 4.4]
        whatFactorsLeadToSwitchToOtherTreatmentOptions: {
          type: "object",
          label: l(
            "11. What factors would lead you to switch a BCC patient from HHI to other treatment options or to discontinue HHI?",
            "11. Quels facteurs vous inciteraient à faire passer un patient atteint de BCC des HHI à d'autres options thérapeutiques ou à interrompre les HHI?"
          ),
          offerNotApplicableForEntireObject: true,
          properties: {
            whatFactorsLeadToSwitchToOtherTreatmentOptionsText: {
              type: "textarea",
              label: "",
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
              hideNotApplicableCheckbox: true,
            },
            factorsListedAboveWhatIsTimeline: {
              type: "textarea",
              label: l(
                "For the factors listed above, what is the timeline for keeping the patient on HHI before switching/discontinuing.",
                "En ce qui concerne les facteurs énumérés ci-dessus, quel est l'échéancier pour maintenir le patient sous HHI avant de changer ou d'interrompre le traitement?"
              ),
              schema: z
                .string({
                  required_error: l("Please enter a value", "Ce champ est requis"),
                  invalid_type_error: l("Please enter a value", "Ce champ est requis"),
                })
                .or(z.literal("not_applicable")),
              hideNotApplicableCheckbox: true,
            },
          },
        },
      },
    },
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        lateStageDoYouHaveAnyAdditionalComments: {
          type: "textarea",
          label: l(
            "Do you have any additional comments, suggestions, or concerns regarding the current version of the schema, particularly in relation to your own personal practice?",
            "Avez-vous d'autres commentaires, suggestions ou préoccupations au sujet de la version actuelle du schéma, particulièrement en ce qui concerne votre pratique personnelle?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l("Please enter a value", "Ce champ est requis"),
            })
            .nullish(),
        },
      },
    },
  ] as const satisfies FormConfigInPages;

export const treatmentLateStageForm: (l: I18NFunction) => SectionConfig = (l) => ({
  sectionId: "treatment-late-stage",
  sectionNumber: 4,
  title: l("Treatment - Late Stage", "Traitement - Stade avancé "),
  description: "",
  diagrams: [
    {
      src: l("/diagrams/treatment_late_cscc.png", "/diagrams/treatment_late_cscc_fr.png"),
      alt: l("Treatment - Late Stage (CSCC)", "Traitement - Stade avancé (CSCC)"),
    },
    {
      src: l("/diagrams/treatment_late_bcc.png", "/diagrams/treatment_late_bcc_fr.png"),
      alt: l("Treatment - Late Stage (BCC)", "Traitement - Stade avancé (BCC)"),
    },
  ],
  formPages: treatmentLateStageFormConfigInPages(l).map((page) => ({
    formConfig: page.formFields,
    formSchemas: formConfigToZodSchemas(page.formFields, l),
    defaultValues: getFormConfigDefaultValues(page.formFields),
  })),
  // formRootKeys: Object.keys(
  //   Object.assign({}, ...treatmentLateStageFormConfigInPages.flatMap((page) => page.formFields))
  // ),
  responseKeyToFormConfig: Object.fromEntries(
    treatmentLateStageFormConfigInPages(l).flatMap((page) =>
      Object.entries(page.formFields).map(([key, field]) => [key, field])
    )
  ),
});
